import React from 'react'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Typography from '@mui/material/Typography'
import { Tooltip } from '@mui/material'
import {
  CheckboxesTypes,
  HandleCheckboxesChangeProps,
} from './EnrollmentsTable'
import { useTranslation } from 'react-i18next'
import EnrollmentRowInfo from '../Interfaces/EnrollmentRowInfo'

export interface LicensingOwedCheckboxesProps {
  enrollment: EnrollmentRowInfo
  isChallenge: boolean
  isEnrollmentsTableEdit: boolean
  handleOnChange: (props: HandleCheckboxesChangeProps) => void
}

const LicensingOwedCheckboxes: React.FC<LicensingOwedCheckboxesProps> = ({
  enrollment,
  isChallenge,
  isEnrollmentsTableEdit,
  handleOnChange,
}) => {
  const { t } = useTranslation()
  const {
    semesterOneTuitionPaid,
    semesterTwoTuitionPaid,
    semesterOneLicensingOwed,
    semesterTwoLicensingOwed,
    studentKey,
    programKey,
    semesterOneExemptReason,
    semesterOneExemptAmount,
    semesterTwoExemptReason,
    semesterTwoExemptAmount,
  } = enrollment

  const isExemptSemesterOne = !!(
    semesterOneExemptAmount && semesterOneExemptReason
  )
  const isExemptSemesterTwo = !!(
    semesterTwoExemptAmount && semesterTwoExemptReason
  )

  const tooltipContent = (exemptAmount?: string, exemptReason?: string) => (
    <>
      <Typography variant="subtitle1">
        {t('LicensingOwedCheckboxes.Tooltip.Title', 'Exempt Information')}
      </Typography>
      <Typography variant="body2">
        {t(
          'LicensingOwedCheckboxes.Tooltip.ExemptAmount',
          'Amount: ${{amount}}',
          { amount: exemptAmount }
        )}
      </Typography>
      <Typography variant="body2">
        {t(
          'LicensingOwedCheckboxes.Tooltip.ExemptReason',
          'Reason: {{reason}}',
          { reason: exemptReason }
        )}
      </Typography>
    </>
  )

  const wrapWithTooltip = (
    element: React.ReactElement,
    exemptAmount?: string,
    exemptReason?: string
  ) => (
    <Tooltip title={tooltipContent(exemptAmount, exemptReason)}>
      {element}
    </Tooltip>
  )

  const licensingOwedCheckBox1stSemester = (
    <FormControlLabel
      control={
        <Checkbox
          name="licensingOwedCheckBox1stSemester"
          color="secondary"
          sx={{ color: 'rgba(0, 0, 0, 0.26)' }}
        />
      }
      aria-label="1st Semester"
      label={
        isChallenge && (
          <Typography variant="caption" component="p">
            {t(
              'LicensingOwedCheckboxes.Checkbox.FirstSemester',
              '1st Semester'
            )}
          </Typography>
        )
      }
      disabled={!isEnrollmentsTableEdit || !semesterOneTuitionPaid}
      checked={semesterOneLicensingOwed}
      onChange={() =>
        handleOnChange({
          studentKey,
          programKey,
          checkboxType: CheckboxesTypes.semesterOneLicensingOwed,
        })
      }
    />
  )
  const licensingOwedCheckBox2ndSemester = (
    <FormControlLabel
      control={
        <Checkbox
          name="licensingOwedCheckBox2ndSemester"
          color="secondary"
          sx={{ color: 'rgba(0, 0, 0, 0.26)' }}
        />
      }
      aria-label="2nd Semester"
      label={
        <Typography variant="caption" component="p">
          {t('LicensingOwedCheckboxes.Checkbox.SecondSemester', '2nd Semester')}
        </Typography>
      }
      disabled={!isEnrollmentsTableEdit || !semesterTwoTuitionPaid}
      checked={semesterTwoLicensingOwed}
      onChange={() =>
        handleOnChange({
          studentKey,
          programKey,
          checkboxType: CheckboxesTypes.semesterTwoLicensingOwed,
        })
      }
    />
  )
  return (
    <FormGroup sx={{ flexDirection: 'row' }}>
      {isExemptSemesterOne
        ? wrapWithTooltip(
            licensingOwedCheckBox1stSemester,
            semesterOneExemptAmount,
            semesterOneExemptReason
          )
        : licensingOwedCheckBox1stSemester}
      {isChallenge &&
        (isExemptSemesterTwo
          ? wrapWithTooltip(
              licensingOwedCheckBox2ndSemester,
              semesterTwoExemptAmount,
              semesterTwoExemptReason
            )
          : licensingOwedCheckBox2ndSemester)}
    </FormGroup>
  )
}

export default LicensingOwedCheckboxes
